import type { SiteDto, TransferDetailDto, TransferProgressDto } from '@pl/domain';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { qk } from '../../lib/queries';
import { Empty, ErrorNote, SiteTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { type FatalTable, kit, type ScanFlowWords, useScanFlow } from './scanKit';

/*
 * What only the transfer screens share. The scan flow itself is `scanKit`'s, used here with a
 * transfer's queue context, its refetch keys and its words.
 */

/** The queue context every scan for one transfer shares, out or in, so one lock covers both. */
export const transferContext = (id: string) => `transfer:${id}`;

/**
 * `POST /scans` answers a floor rule with 200 and a reason code, never an HTTP error. An HTTP error
 * is the server refusing the whole job, and the next box would be refused the same way, so these
 * end the scanning rather than inviting another box.
 */
const TRANSFER_FATAL: FatalTable = {
  TRANSFER_NOT_DRAFT: { title: 'Transfer already sent', note: 'Nothing was changed. This transfer is no longer taking boxes.' },
  TRANSFER_NOT_IN_TRANSIT: { title: 'Nothing on its way', note: 'Nothing was changed. This transfer is not on its way here.' },
  WRONG_SITE: { title: 'Wrong site', note: 'Nothing was changed. This transfer belongs to another site.' },
  NOT_FOUND: { title: 'Transfer not found', note: 'Nothing was changed. This transfer is no longer on the server.' },
  FORBIDDEN: { title: 'Not your site', note: 'Nothing was changed. You do not have access to this transfer’s site.' },
  // The phone mints a fresh id at every capture, so the server only sees a reused one if this app
  // is wrong. Saying "try again" would be a guess; saying what happened is not.
  IDEMPOTENCY_MISMATCH: { title: 'Scan id reused', note: 'Nothing was changed. This is a bug in the app — report it before carrying on.' },
};

/** Where a transfer's red screen sends the worker when the whole job is refused. */
export const BACK_TO_TRANSFERS = { to: '/scan/transfer', label: 'Back to transfers' };

/**
 * Folds a scan's own tally into the cached transfer so the lines move the moment the worker's scan
 * is answered. Only a stopgap: the settle refetch asks the server again as soon as the scan settles,
 * so the phone always ends on the server's numbers, not on its own arithmetic.
 */
export function applyProgress(qc: QueryClient, id: string, progress: TransferProgressDto): void {
  qc.setQueryData<TransferDetailDto>(qk.transfer(id), (prev) => (prev ? { ...prev, status: progress.status, lines: progress.lines, totals: progress.totals } : prev));
}

/** What differs between scanning out and scanning in; the rest is the transfer's. */
export type TransferScanWords = Omit<ScanFlowWords, 'fatal' | 'unchanged' | 'notTaken'>;

/** The shared scan flow, for one transfer: its context, its refetch keys, its fold, its refusals. */
export function useTransferScan(transferId: string, transferCode: string | undefined, words: TransferScanWords) {
  const qc = useQueryClient();
  return useScanFlow(
    {
      context: transferContext(transferId),
      contextId: transferId,
      label: transferCode ?? 'transfer',
      refetchKeys: [qk.transfer(transferId), ['transfers']],
      onResponse: (response) => {
        if (response.transfer) applyProgress(qc, transferId, response.transfer);
      },
    },
    { ...words, fatal: TRANSFER_FATAL, unchanged: 'Nothing was changed', notTaken: 'Nothing was added for this sticker.' },
  );
}

/** "CA → MD", with the full names for a screen reader. */
export function RouteTags({ from, to }: { from: SiteDto | undefined; to: SiteDto | undefined }) {
  return (
    <span className={kit.route} aria-label={`From ${from?.name ?? 'another site'} to ${to?.name ?? 'another site'}`}>
      <SiteTag site={from} />
      <Icon name="arrow-right" size={16} />
      <SiteTag site={to} />
    </span>
  );
}

/**
 * The states a transfer screen shows before it has a transfer to work on: no site on the account,
 * the first load, and a first load that failed. A refresh that fails later is a RefetchNote on the
 * loaded screen instead — replacing the screen for one took the scanner away mid-job.
 */
export function gateTransfer(site: SiteDto | undefined, query: UseQueryResult<TransferDetailDto>): ReactNode | null {
  if (!site) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty title="No site on this account" description="Transfers work from a site. Ask an admin to add you to one." />
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Spinner label="Loading the transfer" />
      </div>
    );
  }
  if (query.isLoadingError || !query.data) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <div className={kit.tkStack}>
          <ErrorNote error={query.error} />
          <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  return null;
}
